import React, { useState } from 'react';
import { CssBaseline, AppBar, Toolbar, Typography, Box, CircularProgress, IconButton, Menu, MenuItem, Avatar, Tooltip } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Import Auth
import { AuthProvider, useAuth } from './components/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ModuleProtectedRoute from './components/ModuleProtectedRoute';

// Import Components
import AppDrawer from './components/AppDrawer';
import BackgroundJobsWidget from './components/BackgroundJobsWidget';
import { ReportJobsProvider } from './contexts/ReportJobsContext';

// Import Pages
// Import Pages
import ObrasListPage from './pages/ObrasListPage';
import FornecedoresPage from './pages/FornecedoresPage';
import InvestidoresPage from './pages/InvestidoresPage';
import CategoriasPage from './pages/CategoriasPage';
import LancamentosPage from './pages/LancamentosPage';
import ObraDetailsPage from './pages/ObraDetailsPage';
import FasesObraPage from './pages/FasesObraPage';
import ItensPage from './pages/ItensPage';
import RelatoriosPage from './pages/RelatoriosPage';
import MemoriaPrecoPage from './pages/MemoriaPrecoPage';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import AdminPage from './pages/AdminPage';
import ModuloManagementPage from './pages/ModuloManagementPage';
import PerfilManagementPage from './pages/PerfilManagementPage';
import ChavesApiPage from './pages/ChavesApiPage';
import AtualizacaoMonetariaPage from './pages/AtualizacaoMonetariaPage';
import InssObraPage from './pages/InssObraPage';
import ProfilePage from './pages/ProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuditLogsPage from './pages/AuditLogsPage';
import AportesResgatesPage from './pages/AportesResgatesPage';
import UnidadesPage from './pages/UnidadesPage';
import RecibosServicoPage from './pages/RecibosServicoPage';
import RecibosMaterialPage from './pages/RecibosMaterialPage';

import AssinaturasPendentesPage from './pages/AssinaturasPendentesPage';
import RecibosAssinadosPage from './pages/RecibosAssinadosPage';
import AssinaturaPage from './pages/public/AssinaturaPage';
import ValidacaoAssinaturaPage from './pages/public/ValidacaoAssinaturaPage';
import NotFoundPage from './pages/NotFoundPage';

import { ThemeProvider } from './components/ThemeContext';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const MainLayout: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/perfil');
  };

  const handleLogout = () => {
    handleClose();
    signOut();
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1" sx={{ mr: 2, fontWeight: 500 }}>
              {user.user_metadata?.name || user.email}
            </Typography>
            <Tooltip title="Configurações da Conta">
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {user.user_metadata?.name ? user.user_metadata.name[0].toUpperCase() : <AccountCircle />}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleProfile}>Meu Perfil</MenuItem>
              <MenuItem onClick={handleLogout}>Sair</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <AppDrawer
        open={drawerOpen}
        onMouseEnter={() => setDrawerOpen(true)}
        onMouseLeave={() => setDrawerOpen(false)}
        onClose={() => setDrawerOpen(false)}
      />
      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3, minHeight: '100vh' }}
      >
        <Toolbar />
        <Routes>
          <Route path="/obras" element={<ModuleProtectedRoute moduleName="Obras"><ObrasListPage /></ModuleProtectedRoute>} />
          <Route path="/fornecedores" element={<ModuleProtectedRoute moduleName="Fornecedores"><FornecedoresPage /></ModuleProtectedRoute>} />
          <Route path="/investidores" element={<ModuleProtectedRoute moduleName="Investidores"><InvestidoresPage /></ModuleProtectedRoute>} />
          <Route path="/categorias" element={<ModuleProtectedRoute moduleName="Categorias"><CategoriasPage /></ModuleProtectedRoute>} />
          <Route path="/fases-de-obra" element={<ModuleProtectedRoute moduleName="Fases da Obra"><FasesObraPage /></ModuleProtectedRoute>} />
          <Route path="/lancamentos" element={<ModuleProtectedRoute moduleName="Lançamentos"><LancamentosPage /></ModuleProtectedRoute>} />
          <Route path="/obras/:id" element={<ModuleProtectedRoute moduleName="Obras"><ObraDetailsPage /></ModuleProtectedRoute>} />
          <Route path="/itens" element={<ModuleProtectedRoute moduleName="Itens"><ItensPage /></ModuleProtectedRoute>} />
          <Route path="/relatorios" element={<ModuleProtectedRoute moduleName="Relatórios"><RelatoriosPage /></ModuleProtectedRoute>} />
          <Route path="/memoria-de-preco" element={<ModuleProtectedRoute moduleName="Memória de Preço"><MemoriaPrecoPage /></ModuleProtectedRoute>} />
          <Route path="/admin" element={<ModuleProtectedRoute moduleName="Administração"><AdminPage /></ModuleProtectedRoute>} />
          <Route path="/admin/modulos" element={<ModuleProtectedRoute moduleName="Módulos"><ModuloManagementPage /></ModuleProtectedRoute>} />
          <Route path="/admin/perfis" element={<ModuleProtectedRoute moduleName="Perfis"><PerfilManagementPage /></ModuleProtectedRoute>} />
          <Route path="/admin/chaves-api" element={<ModuleProtectedRoute moduleName="Administração"><ChavesApiPage /></ModuleProtectedRoute>} />
          <Route path="/admin/auditoria" element={<ModuleProtectedRoute moduleName="Auditoria"><AuditLogsPage /></ModuleProtectedRoute>} />
          <Route path="/calculadoras/atualizacao-monetaria" element={<ModuleProtectedRoute moduleName="Atualização Monetária"><AtualizacaoMonetariaPage /></ModuleProtectedRoute>} />
          <Route path="/calculadoras/inss-obra" element={<ModuleProtectedRoute moduleName="INSS de Obra"><InssObraPage /></ModuleProtectedRoute>} />
          <Route path="/aportes-resgates" element={<ModuleProtectedRoute moduleName="Aportes e Resgates"><AportesResgatesPage /></ModuleProtectedRoute>} />
          <Route path="/unidades" element={<ModuleProtectedRoute moduleName="Unidades"><UnidadesPage /></ModuleProtectedRoute>} />
          <Route path="/recibos/servico" element={<ModuleProtectedRoute moduleName="Recibos"><RecibosServicoPage /></ModuleProtectedRoute>} />
          <Route path="/recibos/material" element={<ModuleProtectedRoute moduleName="Recibos"><RecibosMaterialPage /></ModuleProtectedRoute>} />
          
          <Route path="/assinatura-digital/pendentes" element={<ModuleProtectedRoute moduleName="Assinatura Digital"><AssinaturasPendentesPage /></ModuleProtectedRoute>} />
          <Route path="/assinatura-digital/recibos" element={<ModuleProtectedRoute moduleName="Assinatura Digital"><RecibosAssinadosPage /></ModuleProtectedRoute>} />
          
          <Route path="/perfil" element={<ProfilePage />} />
        </Routes>
        <BackgroundJobsWidget />
      </Box>
    </Box>
  );
}

function DomainHandler() {
  React.useEffect(() => {
    if (window.location.hostname.includes('assinaturas-sistema')) {
      document.title = 'Portal de Assinaturas';
      if (window.location.pathname === '/') {
        window.location.href = 'https://google.com';
      }
    }
  }, []);
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <DomainHandler />
        <AuthProvider>
          <ThemeProvider>
            <CssBaseline />
            <ReportJobsProvider>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/assinatura/:token" element={<AssinaturaPage />} />
                <Route path="/s/:hash" element={<AssinaturaPage />} />
                <Route path="/validacao/:hash" element={<ValidacaoAssinaturaPage />} />
                <Route path="/404" element={<NotFoundPage />} />
                <Route path="/" element={<LandingPage />} />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </ReportJobsProvider>
          </ThemeProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
